import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/api-context';
import { popularMoves } from '../api/opening-explorer';
import type {
  EvaluationLine,
  NodeRef,
  PositionNode,
  RepertoireEdge,
  RepertoireLine,
} from '../api/types';
import { sanLabel } from '../board/notation';
import { ENGINE_LABEL } from '../engine/config';
import { useLocalAnalysisPreference } from '../engine/preference';
import { useEngineSettings } from '../engine/settings';
import { useLocalAnalysis } from '../engine/useLocalAnalysis';
import { formatScore } from '../state/eval';
import { sameNode } from '../state/navigation';

interface Suggestion {
  uci: string;
  san: string;
  personalGames: number;
  popularGames: number;
  /** Local engine line for this move, when it is one of the engine's top choices. */
  engine: EvaluationLine | null;
}

/**
 * Engine lines analysed for this list. More than are shown as engine picks, so the
 * players' moves usually carry an engine score too; depth follows the viewer's setting.
 */
const ENGINE_LINES = 5;
/** Most frequent moves shown: from your games, or Lichess players without them. */
const PLAYER_MOVES = 3;
/** The engine's top-ranked moves, added when not already among the players' moves. */
const ENGINE_MOVES = 2;
/** Earlier iterations reorder too much to be worth showing as suggestions. */
const ENGINE_MIN_DEPTH = 8;

/** Engine scores are side-to-move; shown from White's side like a normal eval. */
function whiteScore(line: EvaluationLine, sideToMove: 'w' | 'b'): string {
  return formatScore(
    sideToMove === 'w' ? line.score : { ...line.score, value: -line.score.value },
  );
}

function responseSaved(
  current: NodeRef,
  uci: string,
  lines: RepertoireLine[],
  ourTurn: boolean,
): boolean {
  return lines.some((line) => line.nodes.some((ref, ply) =>
    sameNode(ref, current) &&
    line.movesUci[ply] === uci &&
    (ourTurn || line.movesUci[ply + 1] !== undefined),
  ));
}

export function MoveSuggestions({
  position,
  legalMoves,
  outgoing,
  lines,
  side,
  onMove,
  busy,
}: {
  position: PositionNode;
  legalMoves: string[];
  outgoing: RepertoireEdge[];
  lines: RepertoireLine[];
  side: 'white' | 'black' | 'both';
  onMove: (uci: string) => Promise<boolean>;
  busy: boolean;
}): JSX.Element {
  const api = useApi();
  const accounts = useQuery({
    queryKey: ['provider-accounts'],
    queryFn: () => api.listProviderAccounts(),
  });
  const ourTurn = side === 'both' || (side === 'white' ? position.sideToMove === 'w' : position.sideToMove === 'b');
  const personal = useQuery({
    queryKey: ['explorer', side, position.canonicalFen],
    queryFn: () => api.explorePosition(position.canonicalFen, side === 'both' ? undefined : side),
    enabled: (accounts.data?.length ?? 0) > 0 && side !== 'both',
    staleTime: 5 * 60_000,
  });
  const popular = useQuery({
    queryKey: ['popular-openings', position.canonicalFen],
    queryFn: ({ signal }) => popularMoves(position.canonicalFen, signal),
    retry: false,
    staleTime: 30 * 60_000,
  });
  // Frequencies run out after the first few moves (and Lichess may refuse the
  // lookup), so the browser engine's top moves keep the builder useful. Advisory
  // and local only, like every other use of this engine (ADR-0015).
  const engineEnabled = useLocalAnalysisPreference();
  const engineSettings = useEngineSettings();
  const engine = useLocalAnalysis(position.canonicalFen, engineEnabled, {
    depth: engineSettings.depth,
    multiPv: ENGINE_LINES,
  });
  const engineLines = engine.depth >= ENGINE_MIN_DEPTH ? engine.lines : [];
  const engineThinking = engine.status === 'checking' ||
    engine.status === 'loading' ||
    (engine.status === 'analysing' && engineLines.length === 0);

  const legal = new Set(legalMoves);
  const byUci = new Map<string, Suggestion>();
  for (const move of popular.data ?? []) {
    if (legal.has(move.uci)) byUci.set(move.uci, {
      uci: move.uci, san: move.san, personalGames: 0, popularGames: move.games, engine: null,
    });
  }
  for (const move of personal.data ?? []) {
    if (!legal.has(move.uci)) continue;
    const existing = byUci.get(move.uci);
    byUci.set(move.uci, {
      uci: move.uci,
      san: move.san,
      personalGames: move.games,
      popularGames: existing?.popularGames ?? 0,
      engine: null,
    });
  }
  for (const line of engineLines) {
    const uci = line.pv[0];
    if (uci === undefined || !legal.has(uci)) continue;
    const existing = byUci.get(uci);
    byUci.set(uci, existing ? { ...existing, engine: line } : {
      uci, san: sanLabel(position.canonicalFen, uci), personalGames: 0, popularGames: 0, engine: line,
    });
  }
  const personalTotal = [...byUci.values()].reduce((sum, move) => sum + move.personalGames, 0);
  const popularTotal = [...byUci.values()].reduce((sum, move) => sum + move.popularGames, 0);
  const current: NodeRef = {
    hashVersion: position.hashVersion,
    zobristHash: position.zobristHash,
    positionId: position.positionId,
  };
  // A short list: the players' most frequent moves, then the engine's best ones.
  const frequency = (move: Suggestion) =>
    personalTotal > 0 ? move.personalGames : move.popularGames;
  const played = [...byUci.values()]
    .filter((move) => frequency(move) > 0)
    .sort((a, b) => frequency(b) - frequency(a) || b.popularGames - a.popularGames)
    .slice(0, PLAYER_MOVES);
  const engineBest = [...byUci.values()]
    .filter((move) => move.engine !== null && move.engine.rank <= ENGINE_MOVES)
    .filter((move) => !played.includes(move))
    .sort((a, b) => (a.engine?.rank ?? 0) - (b.engine?.rank ?? 0));
  const shown = new Set([...played, ...engineBest].map((move) => move.uci));
  // Moves already in the repertoire stay selectable (to switch the continuation),
  // outside the cap: they are choices already made, not new options.
  const saved = outgoing
    .filter((edge) => edge.enabled && legal.has(edge.transition.uci))
    .filter((edge) => !shown.has(edge.transition.uci))
    .map((edge): Suggestion => byUci.get(edge.transition.uci) ?? {
      uci: edge.transition.uci, san: edge.transition.san, personalGames: 0, popularGames: 0,
      engine: null,
    });
  const suggestions = [...played, ...engineBest, ...saved];
  const showsEngine = suggestions.some((move) => move.engine !== null);
  const sample = personalTotal > 0 ? personalTotal : popularTotal;
  const covered = [...byUci.values()].reduce(
    (sum, move) => sum + (responseSaved(current, move.uci, lines, ourTurn)
      ? (personalTotal > 0 ? move.personalGames : move.popularGames) : 0),
    0,
  );

  return (
    <section className="move-suggestions" aria-labelledby="next-move-title">
      <p className="studio-eyebrow">NEXT DECISION</p>
      <h2 id="next-move-title">
        {ourTurn ? 'What would you play here?' : 'Which reply will you prepare for?'}
      </h2>
      <p className="muted">
        {ourTurn
          ? 'Choose a move below or play it on the board. It saves automatically.'
          : 'Add a likely opponent reply, then choose your answer on the next position.'}
      </p>
      {!ourTurn && sample > 0 && (
        <p className="suggestion-coverage">
          Responses ready for {covered.toLocaleString()} of {sample.toLocaleString()} {personalTotal > 0 ? 'occurrences in your imported games' : 'occurrences among these Lichess moves'}.
        </p>
      )}
      {suggestions.length === 0 ? (
        <p className="suggestions-empty">
          {popular.isPending || personal.isFetching || engineThinking
            ? 'Finding useful moves…'
            : 'Play a move on the board to start this line.'}
        </p>
      ) : (
        <ul className="suggestion-list">
          {suggestions.map((move) => {
            const ready = responseSaved(current, move.uci, lines, ourTurn);
            return <li key={move.uci}>
              <button type="button" disabled={busy} onClick={() => void onMove(move.uci)}>
                <strong>{move.san}</strong>
                <span className="suggestion-stats">
                  {move.personalGames > 0 && <span>In your games: {move.personalGames} of {personalTotal}</span>}
                  {move.popularGames > 0 && <span>Lichess: {Math.round(move.popularGames / popularTotal * 100)}%</span>}
                  {move.engine && <span>Engine: {whiteScore(move.engine, position.sideToMove)}</span>}
                </span>
                <span className="suggestion-action">{ready ? 'Saved' : ourTurn ? 'Choose move' : 'Add reply'}</span>
              </button>
            </li>;
          })}
        </ul>
      )}
      {popular.data && popular.data.length > 0 && (
        <p className="suggestion-source">
          Common moves from <a href="https://lichess.org/analysis#explorer" target="_blank" rel="noreferrer">Lichess Opening Explorer</a>. ItsAllPrep sends this position to Lichess for the lookup.
        </p>
      )}
      {showsEngine && (
        <p className="suggestion-source">
          Engine moves come from {ENGINE_LABEL} running in this browser (depth {engine.depth}). They are advisory and never saved.
        </p>
      )}
      {popular.isError && !showsEngine && <p className="suggestion-source">Broad opening frequencies are unavailable right now. You can still build on the board.</p>}
      {engineEnabled && engine.status === 'missing' && suggestions.length === 0 && (
        <p className="suggestion-source">
          Install the browser engine (<code>npm run vendor:engine</code>) to get engine suggestions here.
        </p>
      )}
    </section>
  );
}
